// ================================
// IMPORTS
// ================================
import { setup, assign } from "xstate"
import type { Address } from "@types"
import type { OwnerOutput } from "@core/composables"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Un compteur (source) dans un bâtiment du formulaire multi
 */
export interface SourceFormMultiBuildingSource {
    idtype: string
    idsource: string
    date_from_data?: string | null
}

/**
 * Bâtiment avec adresse et liste de compteurs (aligné avec SourceMultiAppendFormBuildingItem.Building)
 */
export interface SourceFormMultiBuildingItem {
    /** Identifiant stable côté client (clé Vue) */
    id: string
    name: string
    address: Address | null
    location: [number, number] | null
    sources: SourceFormMultiBuildingSource[]
}

/**
 * Données de la demande multi créée (réponse API createConsentRequestMulti)
 */
export interface SourceFormMultiInvitation {
    _id: string
    url?: string
    invalidConsentRequests?: Array<{ idsource: string; idtype: string }>
}

/**
 * Contexte de la machine à états du formulaire multi-compteur
 */
export interface SourceFormMultiContext {
    /** Informations sur le titulaire du compteur */
    owner: Partial<OwnerOutput>
    /** Indique si le compteur est pour une personne tierce (client externe) */
    thirdParty: boolean
    /** Liste des bâtiments à ajouter avec les PDL/PCE */
    buildings: SourceFormMultiBuildingItem[]
    /** Données de la demande créée (après récap) */
    invitation: SourceFormMultiInvitation | null
    /** Index de l'étape courante pour la timeline */
    currentStep: number
    /** Parcours terminé (étape Fin) */
    done: boolean
}

/**
 * Événements de la machine
 */
export type SourceFormMultiEvent =
    | { type: "SUBMIT"; value?: unknown; owner?: Partial<OwnerOutput> }
    | { type: "BACK" }
    | { type: "CHANGE_OWNER" }
    | { type: "SENT" }

// ================================
// MACHINE FACTORY
// ================================

/**
 * Crée la machine à états pour le formulaire d'ajout multi-compteur.
 * États = noms des composants : `SourceFormOwner` → `SourceMultiAppendFormBuilding` → si **tiers** `SourceMultiAppendFormSummary` → `SourceMultiAppendFormInvitationSent` → `SourceMultiAppendFormConsentSign` ; si **titulaire connecté** `SourceMultiAppendFormConsentSign` directement → `SourceMultiAppendFormFinish`.
 *
 * `thirdParty` est dérivé du formulaire titulaire : `!owner.isConnectedUser` (voir `useOwnerForm` / `OwnerOutput`).
 *
 * @returns La machine XState configurée
 */
export function createSourceFormMultiMachine() {
    return setup({
        types: {
            context: {} as SourceFormMultiContext,
            events: {} as SourceFormMultiEvent,
        },
        guards: {
            /** Indique si le titulaire du compteur est une personne tierce */
            didOwnerThirdParty: ({ context }) => !!context.thirdParty,
        },
        actions: {
            incrementStep: assign({
                currentStep: ({ context }) => context.currentStep + 1,
            }),
            decrementStep: assign({
                currentStep: ({ context }) => context.currentStep - 1,
            }),
            decrementStepTwice: assign({
                currentStep: ({ context }) =>
                    Math.max(0, context.currentStep - 2),
            }),
            updateThirdPartyFromSubmittedOwner: assign({
                thirdParty: ({ event }) => {
                    if (event.type !== "SUBMIT") return false
                    const raw = event.owner ?? event.value
                    const o = raw as Partial<OwnerOutput> | undefined
                    return !o?.isConnectedUser
                },
            }),
            updateOwner: assign({
                owner: ({ context, event }) => {
                    if (event.type !== "SUBMIT") return context.owner
                    const value = event.value as
                        | Partial<OwnerOutput>
                        | undefined
                    const ownerPayload = event.owner
                    return {
                        ...context.owner,
                        ...value,
                        ...ownerPayload,
                    }
                },
            }),
            /** Tiers : réponse `createConsentRequestMulti` ; titulaire connecté : `null` (création à l’étape signature) */
            updateInvitation: assign({
                invitation: ({ event }) =>
                    event.type === "SUBMIT" && event.value != null
                        ? (event.value as SourceFormMultiInvitation)
                        : null,
            }),
            updateBuildings: assign({
                buildings: ({ event }) =>
                    (event.type === "SUBMIT" && event.value != null
                        ? (event.value as SourceFormMultiBuildingItem[])
                        : []) ?? [],
            }),
            setDone: assign({
                done: () => true,
            }),
        },
    }).createMachine({
        id: "FormSourceMulti",
        initial: "SourceFormOwner",
        context: {
            owner: {},
            thirdParty: false,
            buildings: [],
            invitation: null,
            currentStep: 1,
            done: false,
        },
        states: {
            SourceFormOwner: {
                on: {
                    SUBMIT: {
                        target: "SourceMultiAppendFormBuilding",
                        actions: [
                            "updateThirdPartyFromSubmittedOwner",
                            "updateOwner",
                            "incrementStep",
                        ],
                    },
                },
            },
            SourceMultiAppendFormBuilding: {
                on: {
                    BACK: {
                        target: "SourceFormOwner",
                        actions: ["decrementStep"],
                    },
                    SUBMIT: [
                        {
                            target: "SourceMultiAppendFormSummary",
                            guard: "didOwnerThirdParty",
                            actions: ["updateBuildings", "incrementStep"],
                        },
                        {
                            target: "SourceMultiAppendFormConsentSign",
                            actions: ["updateBuildings", "incrementStep"],
                        },
                    ],
                },
            },
            SourceMultiAppendFormSummary: {
                on: {
                    BACK: {
                        target: "SourceMultiAppendFormBuilding",
                        actions: ["decrementStep"],
                    },
                    CHANGE_OWNER: {
                        target: "SourceFormOwner",
                        actions: ["decrementStepTwice"],
                    },
                    SUBMIT: [
                        {
                            target: "SourceMultiAppendFormInvitationSent",
                            guard: "didOwnerThirdParty",
                            actions: ["updateInvitation", "incrementStep"],
                        },
                        {
                            target: "SourceMultiAppendFormConsentSign",
                            actions: ["updateInvitation", "incrementStep"],
                        },
                    ],
                },
            },
            SourceMultiAppendFormInvitationSent: {
                on: {
                    SENT: {
                        target: "SourceMultiAppendFormConsentSign",
                        actions: ["incrementStep"],
                    },
                },
            },
            SourceMultiAppendFormConsentSign: {
                on: {
                    BACK: [
                        {
                            guard: "didOwnerThirdParty",
                            target: "SourceMultiAppendFormInvitationSent",
                            actions: ["decrementStep"],
                        },
                        {
                            target: "SourceMultiAppendFormBuilding",
                            actions: ["decrementStep"],
                        },
                    ],
                    SUBMIT: {
                        target: "SourceMultiAppendFormFinish",
                        actions: ["setDone", "incrementStep"],
                    },
                },
            },
            SourceMultiAppendFormFinish: {},
        },
    })
}
